/**
 * Cookie Utilities for Baakas
 * Secure cookie management with proper flags
 */

export interface CookieOptions {
  expires?: number | Date; // Days or Date object
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

const DEFAULT_OPTIONS: CookieOptions = {
  path: '/',
  secure: window.location.protocol === 'https:',
  sameSite: 'Lax',
};

/**
 * Set a cookie with security options
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  // Validate cookie name (no special characters)
  if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
    console.warn('Invalid cookie name:', name);
    return;
  }
  
  // Limit value size (4KB browser limit)
  if (value.length > 4000) {
    console.warn('Cookie value too large:', name);
    return;
  }
  
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  // Handle expires
  if (opts.expires !== undefined) {
    let expiresDate: Date;
    if (typeof opts.expires === 'number') {
      expiresDate = new Date();
      expiresDate.setTime(expiresDate.getTime() + opts.expires * 24 * 60 * 60 * 1000);
    } else {
      expiresDate = opts.expires;
    }
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }
  
  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }
  
  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }
  
  if (opts.secure) {
    cookieString += '; secure';
  }
  
  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  setCookie(name, '', { ...options, expires: -1 });
}

/**
 * Check if cookies are enabled
 */
export function areCookiesEnabled(): boolean {
  try {
    setCookie('__cookie_test__', 'test', { expires: 1 });
    const result = getCookie('__cookie_test__') === 'test';
    deleteCookie('__cookie_test__');
    return result;
  } catch {
    return false;
  }
}

/**
 * Get all cookies as an object
 */
export function getAllCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  
  if (!document.cookie) {
    return cookies;
  }
  
  document.cookie.split(';').forEach((cookie) => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });
  
  return cookies;
}

// Cookie names used in Baakas
export const COOKIE_NAMES = {
  // Essential
  CONSENT: 'baakas_cookie_consent',
  SESSION_ID: 'baakas_session_id',
  
  // Functional
  THEME: 'baakas_theme',
  CACHE_VERSION: 'baakas_cache_v',
  PRELOAD_HINTS: 'baakas_preload',
  
  // Analytics
  ANALYTICS_SESSION: 'baakas_analytics',
  
  // Marketing
  PROMO_TRACKING: 'baakas_promo',
} as const;

// Cookie expiry times (in days)
export const COOKIE_EXPIRY = {
  CONSENT: 365, // 1 year
  SESSION: 1, // 1 day
  FUNCTIONAL: 365, // 1 year
  ANALYTICS: 30, // 30 days
  MARKETING: 90, // 90 days
} as const;
